import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@mui/material';
import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useCurrentSubscriptionQuery } from 'api';
import { GoBackButtonWithTitle, Spinner } from 'components';
import { ContainerW, Title } from 'features/companyDetails/components/CompanyDetails/styled';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useAuth, useWindowSize } from 'hooks';
import { theme } from 'styles';

import { EditCompanyForm, CompanyDetails } from '../../components';
import { useCompanyDetails } from './hooks';
import { Column, ColumnsW, PageHeader, PageW, LinkW, Header } from './styled';

export function CompanyDetailsPage(): ReactElement {
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const { isMobile } = useWindowSize();

  useCurrentSubscriptionQuery();

  const { isLoading, companyDetails, handleOpenModal, handleCloseModal, isOpen, company } =
    useCompanyDetails();

  if (isLoading) {
    return <Spinner />;
  }

  const { generalInfo, cardDetails, contactInfo, subscriptionInfo } = companyDetails;

  return (
    <PageW>
      {!isOpen && (
        <>
          <GoBackButtonWithTitle
            title={t('common.back')}
            containerSx={{
              marginTop: 0,
              maxWidth: '100%',
              marginBottom: '16px',
            }}
          />
          <PageHeader>
            <Header>{t('company.edit.form.title')} </Header>
            {isManager && (
              <CreateButton
                startIcon={<EditIcon style={{ fill: theme.palette.common.white }} />}
                onClick={handleOpenModal}
                style={{
                  height: '56px',
                  width: '200px',
                  padding: '16px',
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.common.white,
                  fontSize: '14px',
                  fontWeight: 400,
                  borderRadius: '8px',
                  textTransform: 'none',
                }}
              >
                {t('company.edit.text')}
              </CreateButton>
            )}
          </PageHeader>

          <ColumnsW>
            <Column>
              <CompanyDetails title={t('company.edit.form.general.title')} data={generalInfo} />
              {isManager && !isMobile && (
                <CompanyDetails
                  title={t('company.edit.form.cardDetails.title')}
                  data={cardDetails}
                />
              )}
              {!isMobile && isManager && (
                <ContainerW>
                  <Title>{t('company.deactivateText')}</Title>

                  <Box sx={{ fontSize: '16px', fontWeight: 600 }}>
                    <Trans i18nKey="company.contactText">
                      <span>Contact our support at </span>
                      <LinkW href="mailto:hello@adligo.io" underline="none">
                        hello@adligo.io
                      </LinkW>
                      <span> to deactivate your company account.</span>
                    </Trans>
                  </Box>
                </ContainerW>
              )}
            </Column>

            <Column>
              <CompanyDetails title={t('company.edit.form.contactInfo.title')} data={contactInfo} />
              {isManager && isMobile && (
                <CompanyDetails
                  title={t('company.edit.form.cardDetails.title')}
                  data={cardDetails}
                />
              )}
              {isManager && (
                <CompanyDetails
                  title={t('company.edit.form.subscriptionDetails.title')}
                  data={subscriptionInfo}
                />
              )}
            </Column>
            {isMobile && isManager && (
              <ContainerW>
                <Title>{t('company.deactivateText')}</Title>

                <Box sx={{ fontSize: '14px', fontWeight: 400 }}>
                  <Trans i18nKey="company.contactText">
                    <span>Contact our support at </span>
                    <a href="mailto:hello@adligo.io">
                      <LinkW underline="none">hello@adligo.io</LinkW>
                    </a>
                    <span> to deactivate your company account.</span>
                  </Trans>
                </Box>
              </ContainerW>
            )}
          </ColumnsW>
        </>
      )}

      {isOpen && company && (
        <EditCompanyForm open={isOpen} onClose={handleCloseModal} company={company} />
      )}
    </PageW>
  );
}
